/**
* Copyright Aquera Inc 2023
*
* This source code is licensed under the BSD-3-Clause license found in the
* LICENSE file in the root directory of this source tree.
*/

import { css } from 'lit';

/**
 * StepperItem CSS
 */
export const styles = css`
	:host {
		--stepper-flex-val:1;
		--bulletin--dot--seperation:30%;
		--lines-thickness: var(--nile-width-1px, var(--ng-border-width-2));
		
		display: inline-block;
		flex-grow:var(--stepper-flex-val);
		min-width: 200px;
		-webkit-font-smoothing: var(--nile-webkit-font-smoothing, var(--ng-webkit-font-smoothing));
    -moz-osx-font-smoothing: var(--nile-moz-osx-font-smoothing, var(--ng-moz-osx-font-smoothing));
    text-rendering: var(--nile-text-rendering, var(--ng-text-rendering));
	}
	
	.stepper__item {
		margin: var( --nile-spacing-none, var(--ng-spacing-none)) -1px;
	}

	.stepper__item--sm {
		--item-spacing: var( --nile-spacing-md, var(--ng-spacing-md));
		--stepper-item-title-size: var( --nile-type-scale-2, var(--ng-font-size-text-sm) );
		--stepper-item-subtitle-size: var( --nile-type-scale-1, var(--ng-font-size-text-sm) );
		--stepper-item-text-line-height: var( --nile-height-16px, var(--ng-line-height-text-sm) );
		--circle-height:18px;
	}

	.stepper__item--lg {
		--item-spacing: var( --nile-spacing-md, var(--ng-spacing-lg));
		--stepper-item-title-size: var( --nile-type-scale-3, var(--ng-font-size-text-sm) );
		--stepper-item-subtitle-size: var( --nile-type-scale-2, var(--ng-font-size-text-sm) );
		--stepper-item-text-line-height: var( --nile-height-18px, var(--ng-line-height-text-md) );
		--circle-height:22px;
	}

	.stepper__item--xl {
		--item-spacing: var( --nile-spacing-lg, var(--ng-spacing-lg));
		--stepper-item-title-size: var( --nile-type-scale-4, var(--ng-font-size-text-md) );
		--stepper-item-subtitle-size: var( --nile-type-scale-3, var(--ng-font-size-text-md) );
		--stepper-item-text-line-height: var( --nile-height-22px, var(--ng-line-height-text-lg) );
		--circle-height:26px;
	}

	.stepper__line__content {
		display:flex;
		width:100%;
		align-items:center;
		gap: var(--nile-spacing-none, var(--ng-spacing-lg));
	}

	.stepper__line__container {
		flex-grow:1;
		padding: var( --nile-spacing-none, var(--ng-spacing-none)) 1px;
	}

	.stepper__line--hastitle {
		display:flex;
		align-items:center;
	}

	.stepper__line {
		display: block;
		border: 0;
		border-top: var(--lines-thickness) solid var(--nile-colors-neutral-500, var(--ng-colors-border-secondary) );
	}

	.stepper__line--active {
		border-top: var(--lines-thickness) solid var( --nile-colors-primary-600, var(--ng-colors-border-brand-alt) );
	}

	.stepper__item__content--above{
		position: relative;
		margin-right: var( --nile-spacing-md, var(--ng-spacing-md));
	}

	.stepper__item__content--above .stepper__content__subtitle{
		position: absolute;
    top: 100%;
    white-space: nowrap;
	}

	.stepper__item__content--below {
		margin-top: var(--item-spacing);
		display:flex;
		flex-direction:column;
		align-items:center;
	}
	
	.stepper__item__bulletin {
		display:grid;
		place-content:center;
		margin: var( --nile-spacing-none, var(--ng-spacing-none)) -1px;
	}

	.stepper__item__bulletin{
		margin-left: var( --nile-spacing-sm, var(--ng-spacing-none));
		margin-right: var( --nile-spacing-sm, var(--ng-spacing-none));
	}
	
	.stepper__item__bulletin--inline{
		margin-right: var( --nile-spacing-sm, var(--ng-spacing-sm));
	}

	.stepper__item--selected .stepper__item__bulletin{
		padding: var(--nile-spacing-none, var(--ng-spacing-xxs));
    	border: var(--nile-spacing-none, var(--ng-border-width-2)) solid var(--ng-colors-effects-focus-ring);
    	border-radius: 50%;
	}

	.stepper__content__title {
		color:var( --nile-colors-dark-900, var(--ng-colors-text-secondary-700));
		font-size: var(--stepper-item-title-size);
		line-height: var(--stepper-item-text-line-height);
		font-family: var(--nile-font-family-medium, var(--ng-font-family-body));
		font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-semibold));
	}

	.stepper__content__title--inline{
		padding: var( --nile-spacing-none, var(--ng-spacing-none)) var(--nile-spacing-md, var(--ng-spacing-md));
		color:var( --nile-colors-dark-900, var(--ng-colors-text-secondary-700) );
	}

	.stepper__content__title--active{
		color:var( --nile-colors-primary-600, var(--ng-colors-text-brand-secondary-700) );
		font-weight: var(--nile-font-weight-semi-bold, var(--ng-font-weight-semibold));
	}

	.stepper__content__subtitle {
		color:var(--nile-colors-dark-500, var(--ng-colors-text-tertiary-600) );
		font-size: var(--stepper-item-subtitle-size);
		line-height: var(--stepper-item-text-line-height);
		font-family: var(--nile-font-family-sans-serif, var(--ng-font-family-body));
		font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
	}

	.stepper__content__subtitle--active{
		color:var( --nile-colors-primary-600, var(--ng-colors-text-brand-tertiary-600) );
	}

	.stepper__bulletin--dot {
		width: var(--circle-height);
		aspect-ratio: 1 / 1;
		border-radius: var(--nile-radius-full, var(--ng-radius-full));
		background: radial-gradient(var(--nile-colors-neutral-500, var(--ng-colors-border-secondary) ) var(--bulletin--dot--seperation), var(--nile-colors-dark-200, var(--ng-colors-bg-primary)) var(--bulletin--dot--seperation));
		border: var(--lines-thickness) solid var(--nile-colors-neutral-500, var(--ng-colors-border-secondary) );
	}

	.stepper__bulletin__dot--active{
		background: radial-gradient(var(--nile-colors-white-base, var(--ng-colors-bg-primary) ) var(--bulletin--dot--seperation), var( --nile-colors-primary-600, var(--ng-colors-bg-brand-solid) ) var(--bulletin--dot--seperation));
		border: var(--lines-thickness) solid var( --nile-colors-primary-500, var(--ng-colors-border-brand-alt) );
		width: calc( var(--circle-height) + calc( var(--lines-thickness) * 2 ) );
	}

	.stepper__bulletin--icon {
		display: grid;
		place-content:center;
		width: var(--circle-height);
		border: var(--lines-thickness) solid var( --nile-colors-primary-600, var(--ng-colors-bg-brand-solid) );
		aspect-ratio: 1 / 1;
		border-radius: var(--nile-radius-full, var(--ng-radius-full));
		background-color:var( --nile-colors-primary-600, var(--ng-colors-bg-brand-solid) );
		overflow:hidden;
	}
	
	.stepper__bulletin__icon--active {
		border: 1px solid var( --nile-colors-primary-500, var(--ng-colors-bg-brand-solid) );
		width: calc(var(--circle-height) + calc( var(--lines-thickness) * 2));
	}
`;
export default [styles];
